/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress, Card, Stack, Box, IconButton } from '@mui/material';
import type { FeatureCollection, Geometry } from 'geojson';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AlertBanner, dateUtils } from '@opengeoweb/shared';
import type { Coordinates } from '@opengeoweb/shared';
import type { CoreAppStore, syncGroupsTypes } from '@opengeoweb/store';
import {
  genericSelectors,
  mapSelectors,
  mapActions,
  genericActions,
  syncGroupsActions,
  useRegisterFeatureSyncSource,
  useSyncgroupTimeState,
} from '@opengeoweb/store';
import { Close } from '@opengeoweb/theme';
import { webmapUtils } from '@opengeoweb/webmap';
import { MapFeatureClass } from '@opengeoweb/webmap-react';
import { SharedMouseYProvider } from './SharedMouseYProvider';
import { SoundingsLegend } from './SoundingsLegend';
import { getTimeMismatchInfo } from './timeMismatchUtils';
import {
  useGetCollectionLocations,
  useMultipleSources,
  useTimesteps,
} from '../../api/edr-api/hooks';
import type {
  FeaturesFromBE,
  SoundingDiagramType,
} from '../../api/edr-api/types';
import { soundingsActions } from '../../store/reducer';
import {
  getDiagramType,
  getSelectedTime,
  getSelectedSources,
  getLocationSelections,
  getSoundingPanelsInOrder,
  getShouldHideLocationMarkers,
  getSoundingsPreset,
  getProfileDataPreferences,
} from '../../store/selectors';
import type {
  SoundingsViewState,
  SoundingsModuleState,
} from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';
import { useSoundingsTranslation } from '../../utils/i18n';
import { MenuBarConnect } from '../MenuBar/MenuBarConnect';
import { SoundingsPanelConnect } from '../SoundingsPanels/SoundingsPanelConnect';
import { SoundingsPanels } from '../SoundingsPanels/SoundingsPanels';
import { SoundingsTimeSliderConnect } from '../SoundingsTimeSlider';
import { TIMESLIDER_HEIGHT } from '../SoundingsTimeSlider/SoundingsTimeSlider';
import { findNearestLocationFeature } from '../utils';

interface SoundingsConnectProps {
  viewId: string;
  soundingsPreset?: SoundingsViewState;
}

export const SoundingsConnect = ({
  viewId,
  soundingsPreset,
}: SoundingsConnectProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();

  const geojsonLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const dispatch = useDispatch();

  const [highlightedFeatureId, setHighlightedFeatureId] = useState<
    string | null
  >(null);

  // Ref to track highlighted feature synchronously (state updates are async)
  const highlightedFeatureIdRef = React.useRef<string | null>(null);

  // Wrapper to update both state and ref together
  const updateHighlightedFeatureId = React.useCallback((id: string | null) => {
    highlightedFeatureIdRef.current = id;
    setHighlightedFeatureId(id);
  }, []);

  const soundingsPresetRef = React.useRef<SoundingsViewState | undefined>(
    undefined,
  );
  const presetJustLoadedRef = React.useRef(false);
  // Flag to prevent re-entrant update cycles between location sync effects
  const isProcessingLocationUpdateRef = React.useRef(false);
  const initialLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});
  const previousLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});

  const currentSoundingsPreset = useSelector((state: SoundingsModuleState) =>
    getSoundingsPreset(state, viewId),
  );

  React.useLayoutEffect(() => {
    dispatch(soundingsActions.registerSoundingsView({ viewId }));
    if (
      soundingsPreset &&
      !isEqual(soundingsPreset, soundingsPresetRef.current)
    ) {
      soundingsPresetRef.current = soundingsPreset;
      // Skip re-registering if the incoming preset already matches the current
      // soundings store state (e.g. workspace initialProps updated after a save)
      if (!isEqual(soundingsPreset, currentSoundingsPreset)) {
        presetJustLoadedRef.current = true;
        initialLocationsRef.current = soundingsPreset.selectedLocations || {};
        previousLocationsRef.current = {};
        dispatch(
          soundingsActions.registerSoundingsPreset({
            viewId,
            preset: soundingsPreset,
          }),
        );
        setTimeout(() => {
          presetJustLoadedRef.current = false;
          initialLocationsRef.current = {};
        }, 500);
      }
    } else if (!soundingsPreset) {
      soundingsPresetRef.current = undefined;
      initialLocationsRef.current = {};
      previousLocationsRef.current = {};
    }
  }, [currentSoundingsPreset, dispatch, soundingsPreset, viewId]);

  useRegisterFeatureSyncSource(viewId);

  const selectedTime = useSelector((state: SoundingsModuleState) =>
    getSelectedTime(state, viewId),
  );
  const rawSourceSelections = useSelector(
    (state: SoundingsModuleState) => getSelectedSources(state, viewId),
    shallowEqual,
  );
  // Stabilize sourceSelections to prevent infinite loops caused by new array references
  const sourceSelectionsRef =
    React.useRef<typeof rawSourceSelections>(undefined);
  const sourceSelections = useMemo(() => {
    if (isEqual(rawSourceSelections, sourceSelectionsRef.current)) {
      return sourceSelectionsRef.current!;
    }
    sourceSelectionsRef.current = rawSourceSelections;
    return rawSourceSelections;
  }, [rawSourceSelections]);

  const locationSelections = useSelector(
    (state: SoundingsModuleState) => getLocationSelections(state, viewId),
    shallowEqual,
  );
  const diagramType = useSelector((state: SoundingsModuleState) =>
    getDiagramType(state, viewId),
  );
  const shouldHideLocationMarkers = useSelector((state: SoundingsModuleState) =>
    getShouldHideLocationMarkers(state, viewId),
  );
  const profileDataPreferences = useSelector((state: SoundingsModuleState) =>
    getProfileDataPreferences(state, viewId),
  );
  const selectedMapIds = useSelector(
    (state: CoreAppStore) => genericSelectors.getLinkedTargetIds(state, viewId),
    shallowEqual,
  );
  const linkedPanelIdsByMapId = useSelector(
    (state: CoreAppStore) =>
      Object.fromEntries(
        selectedMapIds.map((mapId) => [
          mapId,
          genericSelectors.selectLinkedPanelIdsForMap(state, mapId),
        ]),
      ) as Record<string, string[]>,
    shallowEqual,
  );
  const firstLinkedMapId = selectedMapIds[0];
  const { data: sourceTimesteps } = useTimesteps(sourceSelections);
  const syncgroupTimeState = useSyncgroupTimeState(viewId, 0);
  const syncgroupSelectedTime = useMemo(() => {
    if (
      syncgroupTimeState?.current === undefined ||
      !Number.isFinite(syncgroupTimeState.current)
    ) {
      return undefined;
    }
    return dateUtils.fromUnix(syncgroupTimeState.current).toISOString();
  }, [syncgroupTimeState]);
  const isTimeSynced = !!syncgroupSelectedTime;
  const selectedTimeInUse = syncgroupSelectedTime || selectedTime;
  const timeMismatchInfo = useMemo(() => {
    return getTimeMismatchInfo(
      isTimeSynced,
      selectedTimeInUse,
      sourceTimesteps,
    );
  }, [isTimeSynced, selectedTimeInUse, sourceTimesteps]);
  const mismatchKey = timeMismatchInfo ? selectedTimeInUse || '' : '';
  const [isTimeMismatchDismissed, setIsTimeMismatchDismissed] =
    React.useState(false);

  const setChosenDiagramType = React.useCallback(
    (diagramType: SoundingDiagramType) => {
      dispatch(
        soundingsActions.updateDiagramType({
          diagramType,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const selectedFeature = useSelector(
    (state: CoreAppStore) =>
      genericSelectors.getSelectedFeature(state.syncGroups!, viewId),
    shallowEqual,
  );
  // Ref for reading selectedFeature in effects without causing dependency loops
  const selectedFeatureRef = React.useRef(selectedFeature);
  selectedFeatureRef.current = selectedFeature;

  const selectedMapPin = useSelector(
    (state: CoreAppStore) =>
      // mapPins are synced so we can arbitrarily choose the first linked map
      mapSelectors.getPinLocation(state, firstLinkedMapId),
    shallowEqual,
  );
  // Ref for reading selectedMapPin in effects without causing dependency loops
  const selectedMapPinRef = React.useRef(selectedMapPin);
  selectedMapPinRef.current = selectedMapPin;
  interface CollectionItem {
    id: string;
    url: string;
    name: string;
  }
  const collectionsRef = React.useRef<CollectionItem[]>(undefined);
  const collections = useMemo((): CollectionItem[] => {
    const newCollections = sourceSelections.map(
      ({ collectionId, url, name }) => ({
        id: collectionId,
        url,
        name: name || collectionId,
      }),
    );
    if (isEqual(newCollections, collectionsRef.current)) {
      return collectionsRef.current!;
    }
    collectionsRef.current = newCollections;
    return newCollections;
  }, [sourceSelections]);

  const { data: rawLocationData } = useGetCollectionLocations(
    collections,
    true,
  );

  // Stabilize locationData to prevent infinite loops caused by new array references
  const locationDataRef = React.useRef<typeof rawLocationData>(undefined);
  const locationData = useMemo(() => {
    if (isEqual(rawLocationData, locationDataRef.current)) {
      return locationDataRef.current;
    }
    locationDataRef.current = rawLocationData;
    return rawLocationData;
  }, [rawLocationData]);

  // Resolved [lon, lat] per source: string selection → snapped grid feature,
  // array selection → free-position coordinate.
  const sourceGridPoints = useMemo<
    Record<string, [number, number] | undefined>
  >(() => {
    const result: Record<string, [number, number] | undefined> = {};
    sourceSelections.forEach((source) => {
      const selection = locationSelections?.[source.collectionId];
      if (typeof selection === 'string') {
        const sourceLocations = locationData?.find(
          (service) => service.id === source.collectionId,
        );
        const feature = sourceLocations?.features.find(
          (feature) => feature.id === selection,
        );
        if (feature?.geometry.type === 'Point') {
          result[source.collectionId] = feature.geometry.coordinates as [
            number,
            number,
          ];
        }
      } else if (Array.isArray(selection)) {
        result[source.collectionId] = selection;
      }
    });
    return result;
  }, [sourceSelections, locationSelections, locationData]);

  const sourceLocationNames = useMemo<
    Record<string, string | undefined>
  >(() => {
    const result: Record<string, string | undefined> = {};
    sourceSelections.forEach((source) => {
      const selection = locationSelections?.[source.collectionId];
      if (typeof selection !== 'string') {
        return;
      }
      const sourceLocations = locationData?.find(
        (service) => service.id === source.collectionId,
      );
      const feature = sourceLocations?.features.find(
        (feature) => feature.id === selection,
      );
      const name = feature?.properties.name;
      if (name) {
        result[source.collectionId] = name;
      }
    });
    return result;
  }, [sourceSelections, locationSelections, locationData]);

  /* Make sure the map pin is visible when linking new maps */
  useEffect(() => {
    const toggleMapPinVisibility = (displayMapPin: boolean): void => {
      selectedMapIds.forEach((mapId) => {
        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin,
          }),
        );
      });
    };

    toggleMapPinVisibility(true);

    return (): void => {
      selectedMapIds.forEach((mapId) => {
        const linkedPanelIds = linkedPanelIdsByMapId[mapId] ?? [];
        const isStillLinkedToAnotherPanel = linkedPanelIds.some(
          (panelId) => panelId !== viewId,
        );

        if (isStillLinkedToAnotherPanel) {
          return;
        }

        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin: false,
          }),
        );
      });
    };
  }, [dispatch, linkedPanelIdsByMapId, selectedMapIds, viewId]);

  // construct and send geojson to map for drawing locations
  const geoJsonForMapRef =
    React.useRef<
      FeatureCollection<Geometry, syncGroupsTypes.PotentialDataProperties>
    >(undefined);
  const geoJsonForMap = useMemo<
    FeatureCollection<Geometry, syncGroupsTypes.PotentialDataProperties>
  >(() => {
    const locationFeatures = (locationData || []).flatMap(
      ({ id: serviceId, features }) =>
        features.map((feature) => ({
          type: 'Feature' as const,
          geometry: feature.geometry,
          id: feature.id,
          properties: {
            name: feature.properties.name,
            mapFeatureClass: MapFeatureClass.ObservationStation,
            // Green circle for nearest feature when clicking within search radius (but not directly on a station)
            highlightNearbyFeature: highlightedFeatureId === feature.id,
            // Red circle only for directly selected feature (not via nearby click)
            highlightSelectedFeature:
              selectedFeature?.id === feature.id &&
              highlightedFeatureId !== feature.id,
            id: feature.id,
            serviceId,
            hidden: shouldHideLocationMarkers,
          },
        })),
    );

    const newGeoJson = {
      type: 'FeatureCollection' as const,
      features: locationFeatures,
    };

    // Stabilize the reference to prevent unnecessary effect triggers
    if (isEqual(newGeoJson, geoJsonForMapRef.current)) {
      return geoJsonForMapRef.current!;
    }
    geoJsonForMapRef.current = newGeoJson;
    return newGeoJson;
  }, [
    locationData,
    shouldHideLocationMarkers,
    highlightedFeatureId,
    selectedFeature?.id,
  ]);

  useEffect(() => {
    dispatch(
      genericActions.addFeatureSyncState({
        panelId: viewId,
        data: {
          featureLayersById: {
            [geojsonLayerId]: {
              layerId: geojsonLayerId,
              geoJSON: geoJsonForMap,
            },
          },
        },
      }),
    );
  }, [dispatch, geojsonLayerId, geoJsonForMap, viewId]);

  useEffect(
    () => (): void => {
      if (viewId) {
        dispatch(syncGroupsActions.deleteFeatureSyncState({ panelId: viewId }));
      }
    },
    [dispatch, viewId],
  );

  // Map location selection has changed. Update soundings location
  // since the location menu may be out of sync

  // Use ref to avoid circular dependency when reading locationSelections
  // Update ref immediately (not in effect) to ensure it's always current
  const locationSelectionsRef = React.useRef(locationSelections);
  locationSelectionsRef.current = locationSelections;

  // Stabilize targetLocation to prevent infinite loops caused by new array references
  const targetLocationRef = React.useRef<[number, number] | undefined>(
    undefined,
  );
  const targetLocation: [number, number] | undefined = useMemo(() => {
    let newLocation: [number, number] | undefined;
    if (selectedFeature?.lon && selectedFeature.lat) {
      newLocation = [selectedFeature.lon, selectedFeature.lat];
    } else if (selectedMapPin?.lon && selectedMapPin.lat) {
      newLocation = [selectedMapPin.lon, selectedMapPin.lat];
    }
    if (isEqual(newLocation, targetLocationRef.current)) {
      return targetLocationRef.current;
    }
    targetLocationRef.current = newLocation;
    return newLocation;
  }, [
    selectedFeature?.lon,
    selectedFeature?.lat,
    selectedMapPin?.lon,
    selectedMapPin?.lat,
  ]);

  useEffect(() => {
    if (presetJustLoadedRef.current) {
      return;
    }

    // Prevent re-entrant updates from location sync effects
    if (isProcessingLocationUpdateRef.current) {
      return;
    }

    // Clear highlight if no target location
    if (!targetLocation) {
      updateHighlightedFeatureId(null);
      return;
    }

    isProcessingLocationUpdateRef.current = true;

    // Update location for all selected sources
    const currentSelectedFeature = selectedFeatureRef.current;
    const currentMapPin = selectedMapPinRef.current;

    // First pass: find nearest features for each source and collect for highlight
    // We need to find the overall nearest feature across all sources
    const allNearestFeatures: {
      feature: FeaturesFromBE;
      sourceId: string;
    }[] = [];

    sourceSelections.forEach((source) => {
      if (
        source.collectionId &&
        (currentSelectedFeature?.id ||
          (currentMapPin?.lon && currentMapPin.lat))
      ) {
        if (!targetLocation) {
          return;
        }

        // Get features owned by this source
        const sourceData = locationData?.find(
          (service) => service.id === source.collectionId,
        );
        const ownedFeatures = sourceData?.features ?? [];

        // Find the nearest location within search radius that is owned by this source
        const nearestOwnedFeature = findNearestLocationFeature(
          ownedFeatures,
          targetLocation!,
        );

        if (nearestOwnedFeature) {
          allNearestFeatures.push({
            feature: nearestOwnedFeature,
            sourceId: source.collectionId,
          });
        }

        // Use nearest owned location if found, otherwise fall back to clicked position
        const newLocation: string | [number, number] = nearestOwnedFeature
          ? nearestOwnedFeature.id
          : targetLocation!;

        const currentLocation =
          locationSelectionsRef.current[source.collectionId];
        const initialLocation =
          initialLocationsRef.current[source.collectionId];
        if (
          !isEqual(currentLocation, newLocation) &&
          !isEqual(initialLocation, newLocation)
        ) {
          dispatch(
            soundingsActions.updateSelectedLocation({
              sourceId: source.collectionId,
              location: newLocation,
              viewId,
              origin: SoundingsActionOrigin.soundingsComponent,
            }),
          );
        }
      }
    });

    // After processing all sources, determine the highlight:
    // Find the overall nearest feature from all sources that found one
    if (allNearestFeatures.length > 0) {
      // Find the nearest of all nearest features from each source
      const overallNearest = findNearestLocationFeature(
        allNearestFeatures.map((ft) => ft.feature),
        targetLocation!,
      );

      if (overallNearest?.id) {
        // Only set nearby highlight when user clicked on map (not directly on a location marker).
        // Detect direct click by checking if map pin matches the selected feature's coordinates.
        const isDirectClickOnStation =
          currentSelectedFeature?.id === overallNearest.id &&
          currentMapPin?.id === currentSelectedFeature?.id;

        if (isDirectClickOnStation) {
          updateHighlightedFeatureId(null);
        } else {
          updateHighlightedFeatureId(overallNearest.id);
        }
      } else {
        updateHighlightedFeatureId(null);
      }
    } else {
      // No source found a nearby feature
      updateHighlightedFeatureId(null);
    }

    // Reset the processing flag after a timeout to allow React's full update cycle to complete
    setTimeout(() => {
      isProcessingLocationUpdateRef.current = false;
    }, 0);
    // Note: selectedFeature and selectedMapPin are read from refs to avoid dependency cycles
  }, [
    dispatch,
    locationData,
    targetLocation,
    sourceSelections,
    viewId,
    updateHighlightedFeatureId,
  ]);

  // Effect 2 was removed - it was redundant with effect 1 which handles all cases
  // (with or without selectedFeature, falls back to targetLocation if no nearby feature)

  useEffect(() => {
    if (!locationData || locationData.length === 0) {
      return;
    }
    if (selectedMapIds.length === 0) {
      return;
    }

    // Prevent re-entrant updates from location sync effects
    if (isProcessingLocationUpdateRef.current) {
      return;
    }

    const changedSourceIds = sourceSelections.reduce<string[]>(
      (ids, source) => {
        if (!source?.collectionId) {
          return ids;
        }

        const currentLocation = locationSelections[source.collectionId];
        const previousLocation =
          previousLocationsRef.current[source.collectionId];

        if (currentLocation !== previousLocation) {
          return ids.concat(source.collectionId);
        }

        return ids;
      },
      [],
    );

    if (changedSourceIds.length === 0) {
      return;
    }

    isProcessingLocationUpdateRef.current = true;

    changedSourceIds.forEach((changedSourceId) => {
      const selectedLocation = locationSelections[changedSourceId];
      if (!selectedLocation) {
        return;
      }

      previousLocationsRef.current = {
        ...previousLocationsRef.current,
        [changedSourceId]: selectedLocation,
      };

      const serviceData = locationData.find(
        (service) => service.id === changedSourceId,
      );
      if (!serviceData) {
        return;
      }

      if (typeof selectedLocation === 'string') {
        const foundFeature = serviceData.features.find(
          (feature) => feature.id === selectedLocation,
        );
        if (foundFeature?.geometry.type === 'Point') {
          const [lon, lat] = foundFeature.geometry.coordinates;
          // Use refs for synchronous check (state updates are async)
          const isNearestFeatureFromClick =
            highlightedFeatureIdRef.current === selectedLocation;
          const currentMapPin = selectedMapPinRef.current;
          const currentFeature = selectedFeatureRef.current;

          // When a string location is explicitly selected (from menu),
          // always clear the highlight to show red marker instead of green
          if (highlightedFeatureIdRef.current) {
            updateHighlightedFeatureId(null);
          }

          // Skip map pin update only when this is the highlighted nearest feature from a map click
          // AND the pin is already at the correct location
          // Allow update when user selects a different location from menu
          const mapPinNeedsUpdate =
            !isNearestFeatureFromClick &&
            (currentMapPin?.id !== selectedLocation ||
              currentMapPin?.lon !== lon ||
              currentMapPin?.lat !== lat);
          // Skip selectedFeature update for nearby clicks to keep green styling
          const featureNeedsUpdate =
            !isNearestFeatureFromClick &&
            currentFeature?.id !== selectedLocation;

          if (mapPinNeedsUpdate) {
            selectedMapIds.forEach((mapId) => {
              dispatch(
                mapActions.setMapPinLocation({
                  mapId,
                  mapPinLocation: { lon, lat, id: selectedLocation },
                }),
              );
            });
          }

          if (featureNeedsUpdate) {
            dispatch(
              genericActions.addFeatureSyncState({
                panelId: viewId,
                data: { selectedFeatureId: selectedLocation },
              }),
            );
          }
        }
      } else if (Array.isArray(selectedLocation)) {
        const [lon, lat] = selectedLocation;
        const currentMapPin = selectedMapPinRef.current;
        const needsUpdate =
          currentMapPin?.lon !== lon || currentMapPin?.lat !== lat;

        if (needsUpdate) {
          selectedMapIds.forEach((mapId) => {
            dispatch(
              mapActions.setMapPinLocation({
                mapId,
                mapPinLocation: { lon, lat },
              }),
            );
          });
        }
      }
    });

    // Reset the processing flag after a timeout to allow React's full update cycle to complete
    setTimeout(() => {
      isProcessingLocationUpdateRef.current = false;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationData,
    locationSelections,
    sourceSelections,
    selectedMapIds,
    dispatch,
    viewId,
    // Note: highlightedFeatureId intentionally excluded - we use highlightedFeatureIdRef.current
    // inside this effect, and including the state would cause infinite loops since we call
    // updateHighlightedFeatureId within the effect
  ]);

  const { error, data, isPending } = useMultipleSources(
    sourceSelections,
    locationSelections,
    selectedTimeInUse,
  );

  useEffect(() => {
    setIsTimeMismatchDismissed(false);
  }, [mismatchKey]);

  const soundingPanelsInOrder = useSelector((store: SoundingsModuleState) =>
    getSoundingPanelsInOrder(store, viewId),
  );

  const changeOrder = (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ): void => {
    const orderedIds = newOrder.map((collection) => collection.id);
    const currentOrderedIds = soundingPanelsInOrder.map((panel) => panel?.id);
    if (isEqual(orderedIds, currentOrderedIds)) {
      return;
    }
    dispatch(
      soundingsActions.changeSoundingPanelOrder({
        viewId,
        newOrderOfIds: orderedIds,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  };

  return (
    <>
      {!!error && <AlertBanner shouldClose title={error.message} />}
      {!!isPending && (
        <LinearProgress
          color="secondary"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 20,
          }}
        />
      )}
      <Stack
        sx={{
          padding: '12px',
          width: '100%',
          height: '100%',
          backgroundColor: 'geowebColors.background.surfaceApp',
          position: 'relative',
          containerType: 'inline-size',
          containerName: 'soundings-container',
        }}
      >
        <Box paddingLeft={1}>
          <MenuBarConnect viewId={viewId} />
        </Box>
        <Card sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Stack
            spacing={1}
            sx={{ width: '100%', height: '100%', display: 'flex' }}
          >
            {!!timeMismatchInfo && !isTimeMismatchDismissed && (
              <Box sx={{ flex: '0 0 auto', position: 'relative' }}>
                <AlertBanner
                  severity="warning"
                  title={t('soundings-time-mismatch-title')}
                />
                <IconButton
                  aria-label="Close warning"
                  onClick={(): void => setIsTimeMismatchDismissed(true)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 10,
                    padding: 0.25,
                    zIndex: 1,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '2px',
                paddingRight: '2px',
                minHeight: '24px',
                width: '100%',
              }}
            >
              <SoundingsLegend
                selectedPoint={targetLocation}
                sourceGridPoints={sourceGridPoints}
                sourceLocationNames={sourceLocationNames}
                sourceSelections={sourceSelections}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <SharedMouseYProvider>
                <SoundingsPanels
                  chosenDiagramType={diagramType}
                  data={data}
                  onOrderChange={changeOrder}
                  panelComponent={SoundingsPanelConnect}
                  profileDataPreferences={profileDataPreferences}
                  setChosenDiagramType={setChosenDiagramType}
                  soundingPanelsInOrder={soundingPanelsInOrder}
                  viewId={viewId}
                />
              </SharedMouseYProvider>
            </Box>
            <Box
              sx={{
                width: '100%',
                flex: '0 0 auto',
                height: `${TIMESLIDER_HEIGHT}px`,
              }}
            >
              <SoundingsTimeSliderConnect
                syncEnabled={isTimeSynced}
                timeInUse={selectedTimeInUse}
                viewId={viewId}
              />
            </Box>
          </Stack>
        </Card>
      </Stack>
    </>
  );
};
